import { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Ticker from "@/components/Ticker";
import Footer from "@/components/Footer";
import InstallPrompt from "@/components/InstallPrompt";
import HomePage from "@/pages/HomePage";

// Everything except the landing page loads on demand — fans only download the page they visit.
const TeamPage = lazy(() => import("@/pages/TeamPage"));
const NewsArticlePage = lazy(() => import("@/pages/NewsArticlePage"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const RewindPage = lazy(() => import("@/pages/RewindPage"));
const AdvertisePage = lazy(() => import("@/pages/AdvertisePage"));

function RouteFallback() {
  return (
    <main className="flex min-h-[60vh] items-center justify-center" data-testid="route-loading">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-bline border-t-volt" />
    </main>
  );
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/rewind" element={<RewindPage />} />
        <Route path="/rewind/week/:weekKey" element={<RewindPage />} />
        <Route path="/advertise" element={<AdvertisePage />} />
        <Route path="/team/:slug" element={<TeamPage />} />
        <Route path="/news/:id" element={<NewsArticlePage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App min-h-screen bg-ink">
      <BrowserRouter>
        <Navbar />
        <Ticker />
        <AppRouter />
        <Footer />
        <InstallPrompt />
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#11141E", border: "1px solid #1E293B", color: "#F8FAFC" } }} />
    </div>
  );
}

export default App;
